/**
 * Workflow designer component with JSON editor — CoordioCore form-page-pattern.
 *
 * Exposes form state and handlers for the parent WorkflowFormPage to orchestrate
 * via PageFormLayout (ShellHeader + SectionSideNav grid + panel sections).
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + required asterisk)
 * Toggle: ui-form-choice-field CSS classes (label + Switch in horizontal row)
 */
import { useState, useEffect } from 'react';
import { Editor } from '@monaco-editor/react';

import { CoordioDropdown, FormField, Input, Switch, Textarea } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { workflowService } from '../../services/workflowService';
import { useNotificationStore } from '../../store/notificationStore';
import type { CreateWorkflowRequest, UpdateWorkflowRequest } from '../../services/workflowService';

const WORKFLOW_TYPE_OPTIONS = [
  { value: 'Sequential', label: 'Sequential' },
  { value: 'Parallel', label: 'Parallel' },
  { value: 'Conditional', label: 'Conditional' },
  { value: 'Custom', label: 'Custom' },
];

const PATTERN_TYPE_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'ResearchWriteReview', label: 'Research-Write-Review' },
  { value: 'ParallelAnalysis', label: 'Parallel Analysis' },
  { value: 'ConditionalRemediation', label: 'Conditional Remediation' },
  { value: 'HumanInTheLoopApproval', label: 'Human-in-the-Loop' },
  { value: 'RetryWithBackoff', label: 'Retry with Backoff' },
];

interface WorkflowDesignerProps {
  workflowId?: string;
  onSave?: () => void;
  onCancel?: () => void;
}

/**
 * Workflow designer component with JSON editor.
 * Note: A visual drag-and-drop designer using react-flow can be added later.
 */
export const WorkflowDesigner = ({ workflowId, onSave, onCancel }: WorkflowDesignerProps) => {
  const { addNotification } = useNotificationStore();
  const [loading, setLoading] = useState(!!workflowId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [workflowJson, setWorkflowJson] = useState('{\n  "nodes": [],\n  "edges": []\n}');
  const [workflowType, setWorkflowType] = useState('Custom');
  const [patternType, setPatternType] = useState('None');
  const [supportsCheckpointing, setSupportsCheckpointing] = useState(true);
  const [supportsStreaming, setSupportsStreaming] = useState(false);
  const [version, setVersion] = useState('1.0.0');

  useEffect(() => {
    if (workflowId) {
      loadWorkflow();
    }
  }, [workflowId]);

  const loadWorkflow = async () => {
    if (!workflowId) return;
    setLoading(true);
    setError(null);
    try {
      const workflow = await workflowService.getWorkflow(workflowId);
      setName(workflow.name);
      setDescription(workflow.description || '');
      setWorkflowJson(workflow.workflowJson);
      setWorkflowType(workflow.workflowType);
      setPatternType(workflow.patternType);
      setSupportsCheckpointing(workflow.supportsCheckpointing);
      setSupportsStreaming(workflow.supportsStreaming);
      setVersion(workflow.version);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load workflow';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Workflow name is required');
      return;
    }

    try {
      JSON.parse(workflowJson);
    } catch {
      setError('Invalid JSON format');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (workflowId) {
        const updateRequest: UpdateWorkflowRequest = {
          name,
          description,
          workflowJson,
          isActive: true,
          version,
        };
        await workflowService.updateWorkflow(workflowId, updateRequest);
        addNotification('Workflow updated successfully', 'success');
      } else {
        const createRequest: CreateWorkflowRequest = {
          name,
          description,
          workflowJson,
          workflowType: workflowType as any,
          patternType: patternType as any,
          supportsCheckpointing,
          supportsStreaming,
          version,
        };
        await workflowService.createWorkflow(createRequest);
        addNotification('Workflow created successfully', 'success');
      }
      onSave?.();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save workflow';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Expose form state for the parent PageFormLayout to consume. */
  return {
    loading,
    saving,
    error,
    setError,
    handleSave,
    onCancel,

    /** Section content: basic info fields */
    detailsContent: (
      <FormSection title="Workflow identity" description="Name, description, version, and type configuration." data-testid="workflow-form-section-details">
        <div className="grid gap-5 items-end sm:grid-cols-2">
          <FormField label="Name" required description="A unique, descriptive name for this workflow." data-testid="workflow-form-name-field">
            <Input
              className="w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. claims-triage-pipeline"
              data-testid="workflow-form-name"
            />
          </FormField>

          <FormField label="Version" description="Semantic version string for this workflow definition." data-testid="workflow-form-version-field">
            <Input
              className="w-full"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0.0"
              data-testid="workflow-form-version"
            />
          </FormField>
        </div>

        <div className="mt-5">
          <FormField label="Description" description="Optional context about what this workflow does." data-testid="workflow-form-description-field">
            <Textarea
              className="w-full"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Describe the workflow purpose and steps..."
              data-testid="workflow-form-description"
            />
          </FormField>
        </div>

        <div className="grid gap-5 items-end sm:grid-cols-2 mt-5">
          <FormField label="Workflow Type" description="Execution model: Sequential, Parallel, Conditional, or Custom." data-testid="workflow-form-workflowType-field">
            <CoordioDropdown
              options={WORKFLOW_TYPE_OPTIONS}
              value={workflowType}
              onChange={(val) => setWorkflowType(val ?? 'Custom')}
              clearable={false}
              data-testid="workflow-form-workflowType"
            />
          </FormField>

          <FormField label="Pattern Type" description="Pre-built orchestration pattern to apply." data-testid="workflow-form-patternType-field">
            <CoordioDropdown
              options={PATTERN_TYPE_OPTIONS}
              value={patternType}
              onChange={(val) => setPatternType(val ?? 'None')}
              clearable={false}
              data-testid="workflow-form-patternType"
            />
          </FormField>
        </div>
      </FormSection>
    ),

    /** Section content: workflow JSON definition */
    definitionContent: (
      <FormSection title="Workflow JSON" description="Define your workflow as JSON with nodes and edges." data-testid="workflow-form-section-definition">
        <div className="rounded border border-[var(--color-border-default)] overflow-hidden" data-testid="workflow-form-json-editor">
          <Editor
            height="500px"
            defaultLanguage="json"
            value={workflowJson}
            onChange={(value) => setWorkflowJson(value || '')}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: 'on',
            }}
          />
        </div>
      </FormSection>
    ),

    /** Section content: capability toggles */
    capabilitiesContent: (
      <FormSection title="Runtime capabilities" description="Enable or disable advanced execution features." data-testid="workflow-form-section-capabilities">
        <div className="flex flex-col gap-4">
          <div className="ui-form-choice-field">
            <div className="ui-form-choice-field__row">
              <div className="ui-form-choice-field__control">
                <Switch
                  checked={supportsCheckpointing}
                  onCheckedChange={(checked) => setSupportsCheckpointing(checked)}
                  data-testid="workflow-form-supportsCheckpointing"
                />
              </div>
              <div className="ui-form-choice-field__label-cell">
                <span className="ui-form-choice-field__label">Supports Checkpointing</span>
              </div>
            </div>
          </div>

          <div className="ui-form-choice-field">
            <div className="ui-form-choice-field__row">
              <div className="ui-form-choice-field__control">
                <Switch
                  checked={supportsStreaming}
                  onCheckedChange={(checked) => setSupportsStreaming(checked)}
                  data-testid="workflow-form-supportsStreaming"
                />
              </div>
              <div className="ui-form-choice-field__label-cell">
                <span className="ui-form-choice-field__label">Supports Streaming</span>
              </div>
            </div>
          </div>
        </div>
      </FormSection>
    ),
  };
};
